import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { cn } from '@crowdex/ui/lib/utils';
import { Laptop, LoaderCircle, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SHEET_ON_PHONE } from './dialog-sheet';

/** A computer of the user's the conversation can move to (mobile 45). */
export type HandoffDevice = {
  id: string;
  name: string;
  online: boolean;
  /** "~/code/crowdex", or "last seen yesterday" when it is offline. */
  detail: string;
};

type ContinueLocallyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  working: boolean;
  agentName: string | null;
  repositoryName: string | null;
  error: string | null;
  onContinue: () => void;
  conversationTitle?: string | null;
  branch?: string | null;
  /** The local copy it goes to, when known (`~/code/crowdex`). */
  localPath?: string | null;
  /** On a phone: the user's computers, to pick the one it moves to. */
  devices?: readonly HandoffDevice[];
  /** On a phone: copies a link that opens the conversation on a computer instead. */
  onCopyLink?: () => void;
};

/**
 * Continue on this computer (design D48, mobile 45): the existing handoff, which brings the cloud
 * conversation and its changes into the local copy of its repository, the agent keeping its
 * memory. A phone picks the computer among the user's.
 */
export function ContinueLocallyDialog({
  open,
  onOpenChange,
  onCancel,
  working,
  agentName,
  repositoryName,
  error,
  onContinue,
  conversationTitle = null,
  branch = null,
  localPath = null,
  devices,
  onCopyLink,
}: ContinueLocallyDialogProps) {
  const repository = repositoryName?.split('/').at(-1) ?? 'its repository';
  const [device, setDevice] = useState<string | null>(null);
  useEffect(() => {
    if (open) setDevice(devices?.find((candidate) => candidate.online)?.id ?? null);
  }, [open, devices]);
  const chosen = devices?.find((candidate) => candidate.id === device);
  const phone = devices !== undefined;
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className={cn('gap-4 sm:max-w-lg', SHEET_ON_PHONE)} data-handoff-dialog>
        <DialogHeader>
          <DialogTitle className="type-card-title">
            {phone ? 'Continue on a computer' : 'Continue on this computer'}
          </DialogTitle>
          {conversationTitle !== null && conversationTitle !== 'New conversation' && (
            <p className="type-meta text-muted-foreground">{conversationTitle}</p>
          )}
        </DialogHeader>
        <DialogDescription className="text-foreground/85 text-sm leading-6">
          The conversation and its changes
          {branch === null ? '' : ' on '}
          {branch !== null && <code className="font-mono">{branch}</code>} are brought into your
          local copy of {repository}. The agent keeps its memory.
        </DialogDescription>
        {phone ? (
          <fieldset className="space-y-2">
            {devices.map((candidate) => (
              <button
                aria-pressed={device === candidate.id}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors disabled:opacity-60',
                  device === candidate.id
                    ? 'border-foreground/30 bg-control/70'
                    : 'border-hairline hover:bg-control/40',
                )}
                disabled={!candidate.online}
                key={candidate.id}
                onClick={() => setDevice(candidate.id)}
                type="button"
              >
                <Laptop aria-hidden className="size-5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-foreground text-sm">
                    {candidate.name}
                  </span>
                  <span className="type-meta block truncate text-muted-foreground">
                    {candidate.online ? 'Online' : 'Offline'} · {candidate.detail}
                  </span>
                </span>
              </button>
            ))}
          </fieldset>
        ) : (
          <dl className="type-meta space-y-1.5 rounded-xl bg-control/60 px-3.5 py-3">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Local copy</dt>
              <dd className={cn('truncate text-foreground', localPath !== null && 'font-mono')}>
                {localPath ?? 'Chosen as you continue'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Into</dt>
              <dd className={cn('truncate text-foreground', branch !== null && 'font-mono')}>
                {branch ?? 'Its branch'}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Agent</dt>
              <dd className="text-foreground">
                {agentName ?? 'The conversation’s agent'} on this computer
              </dd>
            </div>
          </dl>
        )}
        <p className="type-meta flex items-center gap-2 text-muted-foreground">
          <ShieldCheck aria-hidden className="size-3.5 shrink-0" />
          Uncommitted changes {phone ? 'on the computer' : `in ${localPath ?? 'your copy'}`} stay
          where they are.
        </p>
        {error !== null && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter className="items-center gap-2 max-md:flex-col max-md:items-stretch">
          {!phone && (
            <p className="type-meta mr-auto max-w-48 text-muted-foreground">
              The cloud sandbox stops once it is moved
            </p>
          )}
          {!phone && (
            <Button disabled={working} onClick={onCancel} variant="outline">
              Cancel
            </Button>
          )}
          <Button
            className="rounded-control max-md:h-12"
            disabled={working || (phone && chosen === undefined)}
            onClick={() => onContinue()}
          >
            {working && <LoaderCircle className="size-4 animate-spin" />}
            {working
              ? 'Moving…'
              : phone
                ? `Send to ${chosen?.name ?? 'a computer'}`
                : 'Continue on this computer'}
          </Button>
          {phone && onCopyLink !== undefined && (
            <Button className="rounded-control" onClick={onCopyLink} variant="ghost">
              Copy a link to open it there instead
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
